/**
 * <hy-header-nav>: Verbesserung für das Mega-Menü aus sections/hy-header.liquid.
 * Reine Erweiterung — ohne dieses Modul funktionieren Verschachtelung
 * (:hover/:focus-within), alle Links und der Sticky-Header weiterhin. Es
 * fehlen nur: Klick-Umschalten, Escape-/Klick-außerhalb-Schließen und der
 * mobile Hamburger (die Schublade bleibt dafür offen sichtbar statt
 * unerreichbar). Siehe docs/sections.md, "Geteilte Ebene-3-Komponenten".
 *
 * Die mobile Schublade ist seit dem Header-Redesign (AP-186) ein echter
 * modaler Dialog — bewusste Abkehr von der bisherigen "Disclosure Navigation
 * Menu"-Einordnung (siehe ADR-Fortschreibung, docs/ARCHITEKTUR.md). Seit der
 * Header-Nacharbeit ab 09.09.2026 ist das jetzt auch technisch ein echtes
 * <dialog> (sections/hy-header.liquid, .hy-header__nav-dialog), nicht mehr
 * nur ARIA-simuliert: #setDrawer ruft dialog.showModal()/close() — dieselbe
 * <nav> trägt ab 48rem weiterhin die Desktop-Hover-Panels, deshalb wrappt ein
 * <dialog> nur die <nav>, statt die <nav> selbst zu ersetzen (der Dialog ist
 * auf Desktop per display: contents unsichtbar als Wrapper, showModal() wird
 * dort nie aufgerufen — .hy-header__nav-toggle ist ab 48rem display: none).
 * role="dialog"/aria-modal werden dadurch nicht mehr manuell gesetzt — das
 * native <dialog> bringt beides von sich aus mit, inklusive Top-Layer,
 * Fokus-Falle und einem eigenen ::backdrop. #setDrawer behält NUR die
 * Scroll-Sperre über document.documentElement.style.overflow bei — dieselbe
 * bewusste Ergänzung wie bei assets/hy-cart-drawer.js: natives showModal()
 * sperrt Hintergrund-Scrollen nicht in jedem Browser zuverlässig von sich
 * aus.
 * qs-Fund (Header-Nacharbeit, Sitzung ab 09.09.2026): Eine zusätzliche
 * manuelle Inert-Outside-Markierung (früheres #inertOutside, identisches
 * Ahnenketten-Muster wie assets/hy-facets.js) wurde bewusst NICHT
 * beibehalten — anders als bei der ARIA-simulierten Schublade vor dieser
 * Sitzung nötig, brach sie jetzt, kombiniert mit dem echten showModal(), auf
 * WebKit/iOS Safari die native Fokus-Falle: der Fokus verließ das Dialog
 * nach wenigen Tab-Drücken in Richtung Footer, obwohl `:modal` und
 * `main.inert` beide korrekt `true` waren. assets/hy-cart-drawer.js nutzt
 * aus genau diesem Grund ebenfalls kein manuelles inert und ist auf WebKit
 * nicht betroffen — dieselbe, jetzt auch hier befolgte Lösung.
 * Ein eigener Schließen-Button in der Schublade (sections/hy-header.liquid,
 * data-hy-nav-close) übernimmt den Anfangsfokus — kein reines
 * Komfortelement, WAI-ARIA-APG "Dialog (Modal)" empfiehlt einen erreichbaren
 * internen Schließen-Weg. #desktopQuery erzwingt zusätzlich zu Escape/
 * Schließen-Button das Schließen, sobald der Viewport bei offener Schublade
 * über 48rem wechselt (identischer reviewer-Fund wie PR F2 bei
 * hy-facets.js, sonst bliebe die native Modalität ohne sichtbaren
 * Schließen-Weg hängen — 48rem, nicht 64rem: derselbe Breakpoint wie
 * @media (min-width: 48rem) in sections/hy-header.liquid).
 *
 * Inline-Custom-Properties lösen den einen Fall, den reines CSS nicht kann:
 * ein Klick auf den Disclosure-Button soll ein Panel schließen können, auch
 * während :hover/:focus-within (gleiche Spezifität) es eigentlich offen
 * hielten. Eine Inline-Deklaration schlägt jede Selektor-Regel ohne
 * !important — schließen setzt sie, pointerleave/focusout entfernt sie
 * wieder, sobald Maus/Fokus das Element tatsächlich verlassen.
 *
 * Toggle jetzt außerhalb von <hy-header-nav> (Header-Nacharbeit, Sitzung ab
 * 04.09.2026, Menschen-Wunsch: mobil dieselbe Icon-Reihenfolge wie Desktop
 * in EINER Zeile statt einer eigenen Zeile für den Burger): der Button
 * .hy-header__nav-toggle steht jetzt als letztes Kind in
 * .hy-header__actions (sections/hy-header.liquid), <hy-header-nav> wrappt
 * nur noch die <nav> selbst. Für Desktop folgenlos (Toggle bleibt dort
 * ohnehin display:none) — mobil wird er dadurch ein normales Flex-Kind
 * derselben Icon-Zeile wie Konto/Warenkorb, ganz ohne order/Grid-Trick.
 * Drei Anpassungen dafür nötig, alle unten markiert: (1) der
 * click-Listener für den Toggle hängt jetzt an document statt an this,
 * weil Klicks auf den Toggle sonst nicht mehr durch <hy-header-nav>
 * bubbeln — die Disclosure-Prüfung im selben Handler ist deshalb explizit
 * auf .hy-header__nav .hy-header__disclosure eingegrenzt, damit sie nicht
 * versehentlich auch auf die gleichnamigen Disclosure-Buttons im
 * Sticky-Kompakt-Menü (.hy-header__sticky-menu-panel, wiederverwendet
 * dieselbe .hy-header__menu-Struktur) reagiert. (2) #setDrawer liest/setzt
 * den Toggle über document.querySelector statt this.querySelector. (3) das
 * Sichtbarkeits-Ready-Flag für den Toggle sitzt jetzt direkt auf ihm selbst
 * (data-hy-nav-toggle-ready) statt über eine [data-hy-nav-ready]-Vorfahren-
 * Regel, die ihn als Nachfahren von <hy-header-nav> voraussetzte. Der Toggle
 * liegt dadurch außerhalb von .hy-header__nav — seit der Dialog-Umstellung
 * (s. o.) ohne Wirkung auf seine Erreichbarkeit während die Schublade offen
 * ist: die native Fokus-Falle von showModal() beschränkt Tab-Navigation
 * ohnehin auf den Dialog-Inhalt, unabhängig davon, wo der Toggle im DOM
 * sitzt.
 */
class HyHeaderNav extends HTMLElement {
  #controller = null;
  #desktopQuery = null;
  #dialog = null;

  connectedCallback() {
    this.#controller = new AbortController();
    const { signal } = this.#controller;

    this.setAttribute('data-hy-nav-ready', '');
    // "open" steht im Markup statisch (Kein-JS-Fallback, s.
    // sections/hy-header.liquid). showModal() wirft aber einen
    // InvalidStateError, wenn das open-Attribut bereits gesetzt ist — einmal
    // schließen, sobald JS übernimmt, macht #setDrawer(true) danach sicher
    // benutzbar. Auf Desktop folgenlos: display: contents dort ignoriert den
    // open-Zustand vollständig (s. CSS).
    this.#dialog = this.querySelector('.hy-header__nav-dialog');
    this.#dialog?.close();
    // Ohne JS bleibt der Schließen-Button unsichtbar/unerreichbar (statisches
    // hidden in sections/hy-header.liquid) — ohne Modal gibt es nichts zu
    // schließen, die Fail-Open-Schublade ist ein normaler Block im Fluss.
    this.querySelector('[data-hy-nav-close]')?.removeAttribute('hidden');
    // Toggle liegt jetzt außerhalb von <hy-header-nav> (s. Klassenkommentar)
    // — eigenes Ready-Flag direkt auf dem Button statt einer
    // Vorfahren-Regel.
    document.querySelector('.hy-header__nav-toggle')?.setAttribute('data-hy-nav-toggle-ready', '');

    // Am document statt an this: der Toggle liegt jetzt außerhalb von
    // <hy-header-nav>, Klicks auf ihn bubbeln also nicht mehr durch dieses
    // Element. #handleClick grenzt die Disclosure-Prüfung deshalb explizit
    // auf .hy-header__nav .hy-header__disclosure ein (s. dort).
    document.addEventListener('click', this.#handleClick, { signal });
    this.addEventListener('keydown', this.#handleKeydown, { signal });
    this.addEventListener('focusin', this.#handleFocusIn, { signal });
    this.addEventListener('focusout', this.#handleFocusOut, { signal });
    this.addEventListener('pointerleave', this.#handlePointerLeave, { signal, capture: true });

    document.addEventListener('pointerdown', this.#handleOutsideInteraction, { signal });
    document.addEventListener('focusin', this.#handleOutsideInteraction, { signal });
    this.#dialog?.addEventListener('cancel', this.#handleCancel, { signal });

    // reviewer-Fund PR F2 (hy-facets.js), identisch übernommen: Bleibt die
    // Schublade offen, während der Viewport über 48rem wechselt (Rotation,
    // Fenster-Resize), verschwindet das Overlay per CSS in die Desktop-
    // Hover-Panels zurück, aber der Rest der Seite blieb ohne sichtbaren
    // Schließen-Weg inert — Escape/Schließen-Button sind sonst die einzigen
    // (viewport-unabhängigen) Recovery-Pfade. Schließt hier zusätzlich
    // zwangsweise, sobald der Breakpoint überschritten wird.
    this.#desktopQuery = window.matchMedia('(min-width: 48rem)');
    this.#desktopQuery.addEventListener(
      'change',
      (event) => {
        if (event.matches && this.hasAttribute('data-hy-nav-open')) this.#setDrawer(false);
      },
      { signal },
    );
  }

  disconnectedCallback() {
    // Falls die Schublade gerade offen war (z. B. ein Theme-Editor-
    // Section-Re-Render während der mobilen Vorschau), muss die Scroll-
    // Sperre mit heruntergehen.
    if (this.hasAttribute('data-hy-nav-open')) this.#setDrawer(false);

    this.#controller?.abort();
  }

  #handleClick = (event) => {
    // Auf .hy-header__nav eingegrenzt (der Listener hängt jetzt an document,
    // s. Klassenkommentar): ohne diese Eingrenzung würde derselbe Klick auch
    // auf die gleichnamigen Disclosure-Buttons im Sticky-Kompakt-Menü
    // reagieren (.hy-header__sticky-menu-panel nutzt dieselbe
    // .hy-header__menu-Struktur, absichtlich ohne eigene JS-Klick-Logik —
    // s. sections/hy-header.liquid).
    const disclosure = event.target.closest('.hy-header__nav .hy-header__disclosure');
    if (disclosure) {
      const item = disclosure.closest('.hy-header__item');
      const wasOpen = item.hasAttribute('data-hy-open');
      this.#closeAllItems();
      if (!wasOpen) this.#openItem(item, disclosure);
      return;
    }

    // AP-199 (PR3, Flyout/Mega-Menu-Ebenennavigation): dieselbe
    // Eingrenzung auf .hy-header__nav wie beim regulären Disclosure oben,
    // aus demselben Grund -- die Sticky-Kompaktleiste (.hy-header__sticky-
    // menu) liegt strukturell nie innerhalb von .hy-header__nav (s.
    // sections/hy-header.liquid), unabhängig davon, welchen Menü-Typ sie
    // rendert (seit AP-212 auch echte Flyout-/Mega-Menu-Typen, vorher
    // erzwang ein inzwischen entferntes force_simple immer Dropdown).
    const flyoutDisclosure = event.target.closest('.hy-header__nav .hy-header__flyout-disclosure');
    if (flyoutDisclosure) {
      this.#handleFlyoutDrillIn(flyoutDisclosure);
      return;
    }

    // Header "modern" (AP-268 stage 4): tapping the drawer's search field
    // opens the full-screen search dialog. Close the drawer first (focus goes
    // back to the toggle), then ask assets/hy-predictive-search.js to open
    // the dialog; the toggle becomes its opener for the focus return. Click
    // only, not focus: Tab through the drawer must pass the field and reach
    // the menu (keyboard users keep a plain search form there).
    if (this.hasAttribute('data-hy-nav-open') && event.target.closest('.hy-header__nav [data-hy-search-overlay-trigger] input')) {
      const toggle = document.querySelector('.hy-header__nav-toggle');
      this.#setDrawer(false);
      document.dispatchEvent(new CustomEvent('hy:search:overlay-open', { detail: { opener: toggle } }));
      return;
    }

    if (event.target.closest('.hy-header__nav-toggle')) {
      this.#setDrawer(!this.hasAttribute('data-hy-nav-open'));
      return;
    }

    if (event.target.closest('[data-hy-nav-close]')) {
      this.#setDrawer(false);
    }
  };

  #handleKeydown = (event) => {
    if (event.key === 'Tab' && this.hasAttribute('data-hy-nav-open')) {
      this.#trapTab(event);
      return;
    }

    if (event.key !== 'Escape') return;

    // AP-199 (PR3), ursprünglich gedacht als "Escape geht bei Flyout/Mega-
    // Menu eine Ebene pro Druck zurück, bevor es das ganze Panel schließt".
    // AP-209: data-hy-flyout-active sitzt auf .hy-header__subitem (dem
    // `<li>` von Ebene 2), der Zurück-Button ist entfallen (Punkt 4, Desktop
    // zeigt jetzt beim Hover sofort alles) -- dieser Zweig räumt den
    // Akkordeon-Zustand direkt auf, genau wie ein erneuter Klick auf
    // denselben Chevron es täte.
    // Finding 3 (AP-268 stage 4, known limit since 10.09.2026): the drawer is
    // a real <dialog>, whose native Escape (close request) used to close the
    // whole drawer together with the level. The key press that folds a level
    // is now cancelled, so only the level closes; the next Escape closes the
    // drawer. #handleCancel covers close requests without a key press.
    if (this.#collapseOpenLevel()) {
      if (this.hasAttribute('data-hy-nav-open')) event.preventDefault();
      return;
    }

    if (this.hasAttribute('data-hy-nav-open')) {
      this.#setDrawer(false);
    }
  };

  // Close requests without a key press (Android back gesture) arrive only as
  // 'cancel': fold an open level first there too. Chrome honours
  // preventDefault() on 'cancel' once per user activation (CloseWatcher), so
  // a second back gesture without a tap in between closes the drawer.
  #handleCancel = (event) => {
    if (this.#collapseOpenLevel()) event.preventDefault();
  };

  // Folds the innermost open level (level 3 first, then level 2) and moves
  // focus to its toggle. Returns whether something was open.
  #collapseOpenLevel() {
    const activeSubitem = this.querySelector('.hy-header__subitem[data-hy-flyout-active]');
    if (activeSubitem) {
      activeSubitem.removeAttribute('data-hy-flyout-active');
      const disclosure = activeSubitem.querySelector('.hy-header__flyout-disclosure');
      disclosure?.setAttribute('aria-expanded', 'false');
      disclosure?.focus();
      return true;
    }

    const openItem = this.querySelector('.hy-header__item[data-hy-open]');
    if (openItem) {
      const disclosure = openItem.querySelector('.hy-header__disclosure');
      this.#closeAllItems();
      disclosure?.focus();
      return true;
    }
    return false;
  }

  // qs-Fund (Header-Nacharbeit, Sitzung ab 09.09.2026), per Playwright gegen
  // WebKit empirisch eingegrenzt: die native Fokus-Falle von showModal()
  // verlässt diesen Dialog bereits beim zweiten Tab (Fokus springt vom
  // Suchfeld — einem mittleren, nicht dem letzten Element — direkt auf
  // document.body), unabhängig von Animation/Timing und vom input-Typ.
  // Kein Rand-Wrap-Fehler, den ein reiner Erste/Letzte-Element-Trap fangen
  // würde — WebKit verliert den nächsten Tab-Stop mittendrin. Deshalb
  // übernimmt #trapTab die komplette Tab-Reihenfolge selbst, statt nur an
  // den Rändern einzugreifen: deterministisch in jedem Browser, auf
  // Chromium (native Falle funktioniert dort bereits korrekt) ebenso
  // unauffällig wie auf WebKit.
  #trapTab(event) {
    const dialog = this.#dialog;
    if (!dialog) return;
    const focusable = [
      ...dialog.querySelectorAll(
        'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
      ),
    ].filter((el) => el.offsetParent !== null);
    if (focusable.length === 0) return;

    event.preventDefault();
    const currentIndex = focusable.indexOf(document.activeElement);
    let nextIndex;
    if (currentIndex === -1) {
      nextIndex = event.shiftKey ? focusable.length - 1 : 0;
    } else {
      nextIndex = currentIndex + (event.shiftKey ? -1 : 1);
      if (nextIndex < 0) nextIndex = focusable.length - 1;
      if (nextIndex >= focusable.length) nextIndex = 0;
    }
    focusable[nextIndex].focus();
  }

  #handleFocusIn = (event) => {
    const item = event.target.closest('.hy-header__item');
    const disclosure = item?.querySelector('.hy-header__disclosure');
    // Hält aria-expanded wahrheitsgemäß, auch wenn :focus-within (nicht ein
    // Klick) das Panel geöffnet hat.
    disclosure?.setAttribute('aria-expanded', 'true');
  };

  // Menschen-Fund (10.09.2026): dieser Fokus-Verlassen-schließt-Mechanismus
  // ist für Desktop gedacht (Tab-Ausstieg aus einem :focus-within-Panel soll
  // es schließen) -- auf Mobil führte er (damals in Kombination mit der
  // seither wieder abgelösten vollen Ebenen-Ersetzung, s. Akkordeon-Umbau
  // Punkt 2) zu einem echten, empirisch reproduzierten Bug: ein Klick auf
  // einen Link innerhalb des offenen Panels löste zunächst korrekt einen
  // Fokuswechsel dorthin aus, wurde aber -- je nach Browser -- von einem
  // weiteren, kurz danach folgenden Fokuswechsel überlagert (beobachtet:
  // Fokus landet danach auf einem entfernten Vorfahren-Element), wodurch
  // #closeItem das gerade erst angeklickte Panel wieder schließt und die
  // schon eingeleitete Navigation abbricht -- reproduzierbar auf Chromium
  // UND WebKit. Mobil braucht diesen Mechanismus weiterhin nicht: Schließen
  // läuft dort über den eigenen Disclosure-Chevron (erneuter Klick klappt
  // zu), Escape und Klick außerhalb (s. #handleOutsideInteraction). Deshalb
  // weiterhin nur auf Desktop aktiv -- der ursprüngliche Fund bleibt als
  // Begründung gültig, auch nachdem der Auslöser-Mechanismus selbst
  // ersetzt wurde.
  #handleFocusOut = (event) => {
    if (!this.#desktopQuery.matches) return;
    const item = event.target.closest('.hy-header__item');
    if (!item || item.contains(event.relatedTarget)) return;
    this.#closeItem(item);
  };

  #handlePointerLeave = (event) => {
    const item = event.target.closest?.('.hy-header__item');
    if (!item) return;
    this.#releaseInlineOverride(item);
  };

  #handleOutsideInteraction = (event) => {
    // Ein Klick auf ::backdrop liefert als event.target das <dialog> selbst
    // (kein echter DOM-Knoten für ::backdrop, identisches Verhalten wie bei
    // assets/hy-cart-drawer.js) — this.contains(dialog) ist true, obwohl das
    // klar ein Klick AUSSERHALB des eigentlichen Menü-Inhalts ist. Diese
    // Ausnahme fängt genau den Fall ab, alles andere innerhalb der <nav>
    // bleibt wie bisher "innen". Bewusst auf 'pointerdown' beschränkt: Ein
    // echter Backdrop-Klick kommt immer als pointerdown mit diesem Target.
    // Per Live-Test auf WebKit/iOS (mobil) reproduziert: Tippen auf das
    // <svg>-Icon im Disclosure-Button liefert dort einen focusin mit
    // demselben target === <dialog> (WebKit-Eigenheit beim Fokussieren
    // eines nicht-fokussierbaren Icon-Kindelements), ohne 'pointerdown'
    // ausgeschlossen wäre das fälschlich als Backdrop-Klick gewertet worden
    // und hätte die gerade erst geöffnete Schublade sofort wieder
    // geschlossen — echte Nutzerabsicht war das nie, event.target liegt ja
    // ohnehin innerhalb von <hy-header-nav> (this.contains ist true).
    const isBackdropClick = event.type === 'pointerdown' && event.target === this.#dialog;
    if (this.contains(event.target) && !isBackdropClick) return;
    // Toggle liegt jetzt außerhalb von <hy-header-nav> (s. Klassenkommentar)
    // — ohne diese Ausnahme würde das pointerdown, das jedem Toggle-Klick
    // vorausgeht, die Schublade schon vor #handleClicks eigener
    // Öffnen/Schließen-Logik schließen, ein Schließen-Klick hätte dadurch
    // netto keine Wirkung (schließt via hier, öffnet direkt danach wieder
    // via #handleClick).
    if (event.target.closest('.hy-header__nav-toggle')) return;
    this.#closeAllItems();
    // Only when the drawer is open: #setDrawer(false) also clears the
    // scroll lock on <html>, which another overlay (cart drawer, image view)
    // may have just set before moving focus into its own dialog (AP-272).
    if (this.hasAttribute('data-hy-nav-open')) this.#setDrawer(false);
  };

  #openItem(item, disclosure) {
    this.#releaseInlineOverride(item);
    item.setAttribute('data-hy-open', '');
    disclosure.setAttribute('aria-expanded', 'true');
    // Kein eigener Fokuswechsel mehr (Akkordeon vereinheitlicht, Menschen-Fund
    // 10.09.2026, Punkt 2): der Fokus bleibt auf dem Disclosure-Button, der
    // das Panel geöffnet hat -- WAI-ARIA APG "Disclosure (Show/Hide)"
    // empfiehlt genau das, statt ihn in das gerade aufgeklappte Panel zu
    // schieben.
  }

  #closeItem(item) {
    item.removeAttribute('data-hy-open');
    item.querySelector('.hy-header__disclosure')?.setAttribute('aria-expanded', 'false');
    item.style.setProperty('--hy-nav-panel-visibility', 'hidden');
    item.style.setProperty('--hy-nav-panel-opacity', '0');
    item.style.setProperty('--hy-nav-panel-display', 'none');
    item.style.setProperty('--hy-nav-chevron-rotate', '0deg');
    // AP-199 (PR3): ein erneutes Öffnen desselben Flyout/Mega-Menu-Panels
    // soll immer bei Ebene 1 starten, nicht in der zuletzt aufgerufenen
    // Unterebene stehen bleiben.
    this.#resetFlyoutLevels(item);
  }

  #resetFlyoutLevels(item) {
    for (const subitem of item.querySelectorAll('.hy-header__subitem[data-hy-flyout-active]')) {
      subitem.removeAttribute('data-hy-flyout-active');
    }
    for (const disclosure of item.querySelectorAll('.hy-header__flyout-disclosure')) {
      disclosure.setAttribute('aria-expanded', 'false');
    }
  }

  // Ebene-2-Akkordeon innerhalb eines Flyout-/Mega-Menu-Panels (Menschen-Fund
  // 10.09.2026, Punkt 2: "Akkordeon vereinheitlichen" -- löst den früheren
  // Ebenenwechsel mit voller Ansicht-Ersetzung ab; AP-209: --sub sitzt jetzt
  // direkt im `<li>` von Ebene 2 statt als flaches Geschwister am Listenende,
  // klappt dadurch schon strukturell an der richtigen Stelle auf, s.
  // sections/hy-header.liquid). Zustand als data-hy-flyout-active auf dem
  // `<li>` (.hy-header__subitem) selbst -- CSS liest nur. Nur noch mobil
  // funktional relevant: Desktop-Mega-Menu zeigt beim Hover unbedingt alles
  // (Disclosure-Button dort ausgeblendet, unerreichbar), Desktop-Flyout zeigt
  // sein Ebene-3-Panel seit AP-211 rein über :hover/:focus-within auf
  // .hy-header__subitem (unabhängig von data-hy-flyout-active). Der Chevron
  // ist dort seit AP-213 aus rein visuellen Gründen wieder sichtbar (zeigt
  // "hier gibt es mehr" an) -- reviewer-/qs-Fund (Sitzung ab 10.09.2026):
  // ohne diesen frühen Return hier würde ein Klick/Enter darauf
  // data-hy-flyout-active/aria-expanded setzen, obwohl das auf Desktop keine
  // sichtbare Wirkung mehr hat, UND dieser Zustand könnte hängen bleiben,
  // wenn der Fokus danach weiterwandert (kein Klick löst ihn dort zurück).
  // Gleiches #desktopQuery-Muster wie #handleFocusOut, nur umgekehrte
  // Polarität: dort nur AUF Desktop relevant, hier nur AUSSERHALB.
  #handleFlyoutDrillIn(disclosure) {
    if (this.#desktopQuery.matches) return;
    const subitem = disclosure.closest('.hy-header__subitem');
    if (!subitem) return;
    const targetId = disclosure.getAttribute('aria-controls');
    const target = targetId ? document.getElementById(targetId) : null;
    if (!target) return;

    const item = disclosure.closest('.hy-header__item');
    const wasActive = subitem.hasAttribute('data-hy-flyout-active');

    // Immer nur eine Ebene-3-Liste pro Menüpunkt offen -- eine andere,
    // bereits geöffnete Ebene schließt sich automatisch (echtes
    // Akkordeon-Verhalten statt mehrerer gleichzeitig aufgeklappter Listen).
    for (const sibling of item?.querySelectorAll('.hy-header__subitem[data-hy-flyout-active]') ?? []) {
      if (sibling === subitem) continue;
      sibling.removeAttribute('data-hy-flyout-active');
      sibling.querySelector('.hy-header__flyout-disclosure')?.setAttribute('aria-expanded', 'false');
    }

    if (wasActive) {
      // Erneuter Klick auf denselben Chevron klappt wieder zu.
      subitem.removeAttribute('data-hy-flyout-active');
      disclosure.setAttribute('aria-expanded', 'false');
      return;
    }

    subitem.setAttribute('data-hy-flyout-active', '');
    disclosure.setAttribute('aria-expanded', 'true');
    // Kein eigener Fokuswechsel (WAI-ARIA APG "Disclosure" -- Fokus bleibt
    // auf dem Chevron, der das Panel geöffnet hat, identisches Prinzip wie
    // #openItem auf Ebene 1).
  }

  #releaseInlineOverride(item) {
    item.style.removeProperty('--hy-nav-panel-visibility');
    item.style.removeProperty('--hy-nav-panel-opacity');
    item.style.removeProperty('--hy-nav-panel-display');
    item.style.removeProperty('--hy-nav-chevron-rotate');
  }

  #closeAllItems() {
    for (const item of this.querySelectorAll('.hy-header__item[data-hy-open]')) {
      this.#closeItem(item);
    }
  }

  #setDrawer(open) {
    const wasOpen = this.hasAttribute('data-hy-nav-open');
    this.toggleAttribute('data-hy-nav-open', open);
    // Treibt den echten modalen Zustand des Dialogs an (Top-Layer, natives
    // ::backdrop, role="dialog"/aria-modal kommen dadurch automatisch vom
    // Browser). #dialog ist auf Desktop technisch vorhanden, aber wegen
    // display: contents ohne sichtbaren Effekt — dort wird #setDrawer(true)
    // über den Toggle ohnehin nie ausgelöst (.hy-header__nav-toggle ist
    // display: none ab 48rem).
    // reviewer-Fund: role="none" (statisch im Markup, s. sections/hy-header.
    // liquid) nur für die Dauer des echten Modal-Zustands entfernen — sonst
    // kündigt <dialog open> per impliziter ARIA-Rolle ständig "Dialog" an,
    // auch wenn es (ohne JS, oder ab 48rem) nur ein gewöhnlicher Block ist.
    if (open) {
      this.#dialog?.removeAttribute('role');
      this.#dialog?.showModal();
    } else {
      this.#dialog?.close();
      this.#dialog?.setAttribute('role', 'none');
    }
    // document statt this.querySelector: der Toggle liegt jetzt außerhalb
    // von <hy-header-nav> (s. Klassenkommentar). Nur ein Toggle pro Seite,
    // daher eindeutig.
    document.querySelector('.hy-header__nav-toggle')?.setAttribute('aria-expanded', String(open));

    // Wie assets/hy-cart-drawer.js: nötig, weil showModal() echtes
    // Hintergrund-Scrollen nicht in jedem Browser zuverlässig von sich aus
    // sperrt.
    document.documentElement.style.overflow = open ? 'hidden' : '';

    if (open && !wasOpen) {
      // Anfangsfokus auf den Schließen-Button (WAI-ARIA-APG "Dialog
      // (Modal)"), nicht mehr auf die <nav> selbst: der Toggle liegt jetzt
      // außerhalb von .hy-header__nav und ist damit inert, solange die
      // Schublade offen ist — ohne einen erreichbaren internen
      // Schließen-Weg gäbe es sonst keinen Ausstieg außer Escape.
      this.querySelector('[data-hy-nav-close]')?.focus();
    }

    if (!open) {
      this.#closeAllItems();
      // Fokus-Rückgabe an den Toggle nur, wenn er noch in der (gerade
      // geschlossenen) Schublade lag oder ins Nichts gefallen ist — ein Klick
      // außerhalb auf einen echten Link/Button (#handleOutsideInteraction)
      // soll den Fokus dort lassen, wo der Nutzer hin wollte. close() nimmt
      // dem zuvor fokussierten Element (innerhalb des jetzt display:none
      // gesetzten Dialogs) die Fokussierbarkeit — der Browser fällt in
      // diesem Fall auf document.body zurück, wenn kein anderer Klick
      // dazwischen bereits woanders fokussiert hat.
      if (wasOpen && (this.contains(document.activeElement) || document.activeElement === document.body)) {
        document.querySelector('.hy-header__nav-toggle')?.focus();
      }
    }
  }

}

customElements.define('hy-header-nav', HyHeaderNav);
